import { fg } from '@atlaskit/platform-feature-flags/fg';
import { requestWithAbort, type RequestResponse } from '@atlassian/twg-common/request';

import type {
	NL2CypherResponse,
	QueryNL2CypherPayload,
	QuerySuggestionsResponse,
} from '../../types';
import { logMessage } from '../sentry-observability';

const openAIModel: QueryNL2CypherPayload['model'] = {
	family: 'gpt',
	version: '4.1-2025-04-14',
};

const claudeModel: QueryNL2CypherPayload['model'] = {
	family: 'claude',
	version: 'latest',
};

export const queryNL2Cypher = (
	query: string,
	cloudId: string,
	cypherOnly: boolean = false,
): {
	controller: AbortController;
	execute: Promise<RequestResponse<NL2CypherResponse>>;
} => {
	const useOpenAI = fg('twg_query_builder_with_openai');

	if (query.length === 0) {
		logMessage('queryNL2Cypher called with empty query');
	}

	const payload: QueryNL2CypherPayload = {
		model: useOpenAI ? openAIModel : claudeModel,
		messages: [
			{
				role: 'user',
				content: [
					{
						type: 'text',
						text: query,
					},
				],
			},
		],
	};

	const { controller, execute } = requestWithAbort<NL2CypherResponse>(
		'post',
		`/gateway/api/twg-api-v2/${cloudId}/v2/nl2cypher/chat/completions`,
		payload,
		{
			credentials: 'include',
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Query-Context': getWorkspaceAri(cloudId),
			...(cypherOnly ? { 'X-Cypher-Only': 'true' } : {}),
		},
		[200, 400], // 200 => good, 400 => bad query / couldn't generate cypher
	);
	return { controller, execute };
};

export const querySuggestions = (
	query: string,
	cloudId: string,
	reason: string | null = '',
): {
	controller: AbortController;
	execute: Promise<RequestResponse<QuerySuggestionsResponse>>;
} => {
	const { controller, execute } = requestWithAbort<QuerySuggestionsResponse>(
		'post',
		`/gateway/api/twg-api-v2/${cloudId}/v1/nl2cypher/query-suggestions`,
		{
			query,
			reason,
		},
		{
			credentials: 'include',
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Query-Context': getWorkspaceAri(cloudId),
		},
	);

	return { controller, execute };
};

export const getWorkspaceAri = (cloudId: string) => `ari:cloud:platform::site/${cloudId}`;
